import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Search, Users, MapPin, BookOpen, Filter, Eye, Send, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { teamAPI, hackathonAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { TeamWithDetails } from '../../types/events';

// Separate SearchInput component to prevent re-rendering
const SearchInput = React.memo(({ value, onChange }: { value: string; onChange: (value: string) => void }) => {
  return (
    <div className="relative flex-1 lg:max-w-md">
      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
      <Input
        placeholder="Search teams, skills, members, or problem statements..."
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="pl-10"
      />
    </div>
  );
});

interface Hackathon {
  _id: string;
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  status: string;
  problemStatements: Array<{
    _id: string;
    title: string;
    description: string;
    category: string;
    difficulty: string;
  }>;
}

interface Filters {
  search: string;
  filterType: 'all' | 'lookingForMembers' | 'myTeam';
}

const TeamDiscovery: React.FC = () => {
  const { hackathonId } = useParams<{ hackathonId: string }>();
  const navigate = useNavigate();
  const { success, error } = useToast();
  const { user } = useAuth();
  
  const [hackathon, setHackathon] = useState<Hackathon | null>(null);
  const [allTeams, setAllTeams] = useState<TeamWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState<string | null>(null);
  const [filters, setFilters] = useState<Filters>({
    search: '',
    filterType: 'all'
  });
  const [currentPage, setCurrentPage] = useState(1);
  
  // Application modal state
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<TeamWithDetails | null>(null);
  const [applicationNotes, setApplicationNotes] = useState('');

  // Fetch hackathon details
  useEffect(() => {
    if (hackathonId) {
      fetchHackathon();
      fetchTeams();
    }
  }, [hackathonId]);

  const fetchHackathon = async () => {
    try {
      const data = await hackathonAPI.getHackathonById(hackathonId!);
      setHackathon(data);
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to fetch hackathon details');
    }
  };

  const fetchTeams = async () => {
    try {
      setLoading(true);
      const data = await teamAPI.browseHackathonTeams(hackathonId!, {
        page: 1,
        limit: 100 // Get all teams for client-side filtering
      });
      setAllTeams(data.teams);
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to fetch teams');
    } finally {
      setLoading(false);
    }
  };

  // Client-side filtering like AdminDashboard
  const filteredTeams = allTeams.filter(team => {
    const searchTermLower = filters.search.toLowerCase();
    const matchesSearch = filters.search === '' || 
      team.name.toLowerCase().includes(searchTermLower) ||
      team.teamNotes?.toLowerCase().includes(searchTermLower) ||
      team.leader?.firstName?.toLowerCase().includes(searchTermLower) ||
      team.leader?.lastName?.toLowerCase().includes(searchTermLower) ||
      team.members?.some(member => 
        member.user?.firstName?.toLowerCase().includes(searchTermLower) ||
        member.user?.lastName?.toLowerCase().includes(searchTermLower) ||
        member.user?.skills?.technical?.some(skill => skill.toLowerCase().includes(searchTermLower)) ||
        member.user?.skills?.soft?.some(skill => skill.toLowerCase().includes(searchTermLower)) ||
        member.user?.primarySkills?.some(skill => skill.toLowerCase().includes(searchTermLower)) ||
        member.user?.secondarySkills?.some(skill => skill.toLowerCase().includes(searchTermLower))
      ) ||
      team.selectedProblemStatement?.title?.toLowerCase().includes(searchTermLower) ||
      team.selectedProblemStatement?.description?.toLowerCase().includes(searchTermLower) ||
      team.assignedProblemStatement?.title?.toLowerCase().includes(searchTermLower) ||
      team.assignedProblemStatement?.description?.toLowerCase().includes(searchTermLower);

    const matchesFilterType = 
      filters.filterType === 'all' ||
      (filters.filterType === 'lookingForMembers' && team.isLookingForMembers) ||
      (filters.filterType === 'myTeam' && (
        team.leader?._id === user?._id ||
        team.members?.some(member => member.user?._id === user?._id) ||
        team.userApplicationStatus?.status === 'pending' ||
        team.userApplicationStatus?.status === 'accepted' ||
        team.userApplicationStatus?.status === 'member'
      ));

    return matchesSearch && matchesFilterType;
  });

  const handleFilterChange = (key: keyof Filters, value: string | 'all' | 'lookingForMembers' | 'myTeam') => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Pagination logic
  const itemsPerPage = 10;
  const totalPages = Math.ceil(filteredTeams.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedTeams = filteredTeams.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleApplyToTeam = async () => {
    if (!selectedTeam) return;

    try {
      setApplying(selectedTeam._id);
      await teamAPI.applyToTeam(selectedTeam._id, applicationNotes);
      success('Application submitted successfully!');
      setShowApplicationModal(false);
      setSelectedTeam(null);
      setApplicationNotes('');
      fetchTeams(); // Refresh teams to update application status
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to submit application');
    } finally {
      setApplying(null);
    }
  };

  const handleWithdrawApplication = async (teamId: string) => {
    try {
      await teamAPI.withdrawApplication(teamId);
      success('Application withdrawn successfully!');
      fetchTeams(); // Refresh teams to update application status
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to withdraw application');
    }
  };

  const getApplicationStatusBadge = (status: string, team?: TeamWithDetails) => {
    switch (status) {
      case 'member':
        // Check if user is the team leader
        if (team && team.leader._id === user?._id) {
          return <Badge className="bg-blue-100 text-blue-800">Leader</Badge>;
        }
        return <Badge className="bg-green-100 text-green-800">Member</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800">Applied - Waiting for approval</Badge>;
      case 'accepted':
        return <Badge className="bg-green-100 text-green-800">Accepted</Badge>;
      case 'rejected':
        // Don't show rejected badge - allow users to apply again
        return null;
      default:
        return null;
    }
  };

  const hasAppliedToTeam = (team: TeamWithDetails) => {
    // Only consider pending or accepted applications as "applied"
    // Rejected applications should not prevent applying again
    return team.userApplicationStatus?.status === 'pending' || 
           team.userApplicationStatus?.status === 'accepted';
  };

  const canApplyToTeam = (team: TeamWithDetails) => {
    // User can apply if:
    // 1. They haven't applied to any other team (pending/accepted/member)
    // 2. This team is looking for members
    // 3. They are not already a member of this team
    // 4. They can apply even if previously rejected (status: 'rejected' or 'not_applied')
    return !hasAppliedToAnyTeam() && 
           team.userApplicationStatus?.status !== 'member' && 
           team.userApplicationStatus?.status !== 'pending' && 
           team.userApplicationStatus?.status !== 'accepted' && 
           team.isLookingForMembers;
  };

  // Check if user has applied to any team
  const hasAppliedToAnyTeam = () => {
    return allTeams.some(team => 
      team.userApplicationStatus?.status === 'pending' || 
      team.userApplicationStatus?.status === 'accepted' ||
      team.userApplicationStatus?.status === 'member'
    );
  };

  // Get user's current application status for display
  const getUserApplicationStatus = () => {
    const appliedTeam = allTeams.find(team => 
      team.userApplicationStatus?.status === 'pending' || 
      team.userApplicationStatus?.status === 'accepted' ||
      team.userApplicationStatus?.status === 'member'
    );

    if (!appliedTeam) return null;

    const status = appliedTeam.userApplicationStatus?.status;
    const teamName = appliedTeam.name;

    if (status === 'member') {
      return { type: 'member', message: `You are a member of ${teamName}` };
    } else if (status === 'accepted') {
      return { type: 'accepted', message: `Your application to ${teamName} has been accepted` };
    } else if (status === 'pending') {
      return { type: 'pending', message: `You have applied to ${teamName} - Status: Pending` };
    }

    return null;
  };

  const getMemberSkills = (member: any) => {
    const skills = [];
    if (member.skills?.technical) skills.push(...member.skills.technical);
    if (member.skills?.soft) skills.push(...member.skills.soft);
    if (member.primarySkills) skills.push(...member.primarySkills);
    if (member.secondarySkills) skills.push(...member.secondarySkills);
    return [...new Set(skills)].slice(0, 3); // Show max 3 unique skills
  };

  const getMemberExperience = (member: any) => {
    if (member.professionalInfo?.yearsOfExperience) {
      return `${member.professionalInfo.yearsOfExperience} years`;
    }
    if (member.education?.graduationYear) {
      const currentYear = new Date().getFullYear();
      const yearsSinceGraduation = currentYear - member.education.graduationYear;
      if (yearsSinceGraduation >= 0) {
        return yearsSinceGraduation === 0 ? 'Student' : `${yearsSinceGraduation} years`;
      }
    }
    return 'Student';
  };

  const getMemberLocation = (member: any) => {
    if (!member.address) return 'Location not specified';
    
    // Try to extract city from address
    // Address format is typically: "H.No 10-332/2, Vasanthapuri Colony, Street # 11, Malkajgiri, Malkajgiri, Hyderabad, Telangana, India, 500047"
    const addressParts = member.address.split(',').map((part: string) => part.trim());
    
    // Look for city (usually the 5th or 6th part from the end)
    // Common city patterns: Hyderabad, Mumbai, Delhi, etc.
    for (let i = addressParts.length - 1; i >= 0; i--) {
      const part = addressParts[i];
      // Skip postal codes, state names, and country
      if (!/^\d{6}$/.test(part) && 
          !['India', 'Telangana', 'Maharashtra', 'Delhi', 'Karnataka', 'Tamil Nadu', 'Kerala', 'Andhra Pradesh'].includes(part)) {
        return part;
      }
    }
    
    // Fallback to the last part that's not a postal code or country
    return addressParts[addressParts.length - 2] || 'Location not specified';
  };

  if (loading && allTeams.length === 0) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Team Discovery
            </h1>
            {hackathon && (
              <p className="text-gray-600">
                Browse teams for: <span className="font-semibold">{hackathon.title}</span>
              </p>
            )}
          </div>
          
          {/* User Application Status */}
          {getUserApplicationStatus() && (
            <div className="text-left lg:text-right">
              <div className={`inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium ${
                getUserApplicationStatus()?.type === 'member' 
                  ? 'bg-green-100 text-green-800' 
                  : getUserApplicationStatus()?.type === 'accepted'
                  ? 'bg-blue-100 text-blue-800'
                  : 'bg-yellow-100 text-yellow-800'
              }`}>
                {getUserApplicationStatus()?.type === 'member' && <CheckCircle className="h-4 w-4 mr-2" />}
                {getUserApplicationStatus()?.type === 'accepted' && <CheckCircle className="h-4 w-4 mr-2" />}
                {getUserApplicationStatus()?.type === 'pending' && <Clock className="h-4 w-4 mr-2" />}
                {getUserApplicationStatus()?.message}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Search & Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col lg:flex-row gap-4 items-start">
            {/* Search */}
            <SearchInput
              value={filters.search}
              onChange={(value) => handleFilterChange('search', value)}
            />

            {/* Filter Type */}
            <div className="flex flex-wrap gap-2">
              <Button
                variant={filters.filterType === 'all' ? 'default' : 'outline'}
                onClick={() => handleFilterChange('filterType', 'all')}
                size="sm"
              >
                All Teams
              </Button>
              <Button
                variant={filters.filterType === 'lookingForMembers' ? 'default' : 'outline'}
                onClick={() => handleFilterChange('filterType', 'lookingForMembers')}
                size="sm"
              >
                Looking for Members
              </Button>
              <Button
                variant={filters.filterType === 'myTeam' ? 'default' : 'outline'}
                onClick={() => handleFilterChange('filterType', 'myTeam')}
                size="sm"
              >
                My Teams & Applications
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            Teams ({filteredTeams.length})
          </h2>
        </div>

        {paginatedTeams.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Users className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No teams found
              </h3>
              <p className="text-gray-600">
                {totalPages === 0 
                  ? 'No teams are available for this hackathon yet.'
                  : filters.filterType === 'myTeam'
                  ? 'You haven\'t joined or applied to any teams yet.'
                  : 'No teams match your current filters. Try adjusting your search criteria.'
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 lg:gap-6">
            {paginatedTeams.map((team) => (
              <Card key={team._id} className="hover:shadow-lg transition-shadow flex flex-col h-full">
                <CardHeader>
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
                    <div>
                      <CardTitle className="text-lg">{team.name}</CardTitle>
                      <p className="text-sm text-gray-600">
                        {team.members.length}/{team.hackathon.maxTeamSize} members
                        {team.pendingApplications && team.pendingApplications.filter(app => app.status !== 'rejected').length > 0 && (
                          <span>
                            {' '}(applications pending for review - {team.pendingApplications.filter(app => app.status !== 'rejected').length})
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="flex flex-col sm:items-end gap-2">
                      {team.isLookingForMembers && (
                        <Badge className="bg-blue-100 text-blue-800">Looking for members</Badge>
                      )}
                      {team.userApplicationStatus && getApplicationStatusBadge(team.userApplicationStatus.status, team)}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4 flex flex-col flex-1">
                  {/* Team Leader */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Team Leader</h4>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        <span>{team.leader.firstName} {team.leader.lastName}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="hidden sm:inline">•</span>
                        <span>{team.leader.email}</span>
                      </div>
                      {team.leader.phone && (
                        <div className="flex items-center gap-2">
                          <span className="hidden sm:inline">•</span>
                          <span>{team.leader.phone}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Team Members */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Team Members</h4>
                    <div className="space-y-2">
                      {team.members.map((member) => (
                        <div key={member.user._id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                            <span className="font-medium">
                              {member.user.firstName} {member.user.lastName}
                            </span>
                            <span className="hidden sm:inline text-gray-500">•</span>
                            <span className="text-gray-600">{getMemberExperience(member.user)}</span>
                            <span className="hidden sm:inline text-gray-500">•</span>
                            <span className="text-gray-600">{getMemberLocation(member.user)}</span>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {getMemberSkills(member.user).map((skill, index) => (
                              <Badge key={index} variant="outline" className="text-xs">
                                {skill}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Problem Statement */}
                  {(team.selectedProblemStatement || team.assignedProblemStatement) && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Problem Statement</h4>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <BookOpen className="h-4 w-4" />
                        <span>
                          {team.assignedProblemStatement?.title || team.selectedProblemStatement?.title}
                        </span>
                        {team.assignedProblemStatement && (
                          <Badge className="bg-purple-100 text-purple-800 text-xs">Assigned</Badge>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Mentor */}
                  {team.assignedMentor && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Mentor</h4>
                      <div className="text-sm text-gray-600">
                        {team.assignedMentor.firstName} {team.assignedMentor.lastName}
                      </div>
                    </div>
                  )}

                  {/* Team Notes */}
                  {team.teamNotes && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Team Notes</h4>
                      <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                        {team.teamNotes}
                      </p>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-2 pt-4 border-t mt-auto">
                    {canApplyToTeam(team) && (
                      <Button
                        onClick={() => {
                          setSelectedTeam(team);
                          setShowApplicationModal(true);
                        }}
                        className="w-full sm:flex-1"
                      >
                        <Send className="h-4 w-4 mr-2" />
                        Apply to Team
                      </Button>
                    )}
                    
                    {!canApplyToTeam(team) && !hasAppliedToTeam(team) && team.userApplicationStatus?.status !== 'member' && team.isLookingForMembers && (
                      <Button
                        disabled
                        variant="outline"
                        className="w-full sm:flex-1 opacity-50 cursor-not-allowed"
                        title={hasAppliedToAnyTeam() ? "You can only apply to one team at a time" : "Team is not looking for members"}
                      >
                        <AlertCircle className="h-4 w-4 mr-2" />
                        {hasAppliedToAnyTeam() ? "Already Applied to Another Team" : "Team Not Accepting Applications"}
                      </Button>
                    )}
                    
                    {hasAppliedToTeam(team) && (
                      <Button
                        onClick={() => handleWithdrawApplication(team._id)}
                        variant="outline"
                        className="w-full sm:flex-1"
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Withdraw Application
                      </Button>
                    )}
                    
                    {team.userApplicationStatus?.status === 'member' && (
                      <Button
                        onClick={() => navigate(`/teams/${team._id}`)}
                        className="w-full sm:flex-1"
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        View Team
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row justify-center items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </Button>
          <span className="text-sm text-gray-600 text-center">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            variant="outline"
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
        </div>
      )}

      {/* Application Modal */}
      <Dialog open={showApplicationModal} onOpenChange={setShowApplicationModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Apply to Team</DialogTitle>
            <DialogDescription>
              Send an application to join {selectedTeam?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="notes">Application Notes (Optional)</Label>
              <Textarea
                id="notes"
                placeholder="Tell the team leader why you'd like to join and what you can contribute..."
                value={applicationNotes}
                onChange={(e) => setApplicationNotes(e.target.value)}
                rows={4}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowApplicationModal(false);
                setSelectedTeam(null);
                setApplicationNotes('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleApplyToTeam}
              disabled={applying === selectedTeam?._id}
            >
              {applying === selectedTeam?._id ? (
                <>
                  <LoadingSpinner size="sm" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Submit Application
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamDiscovery;